/**
 * CartBar — a sticky bottom bar that appears on services-related pages
 * whenever the cart has items, so the user can jump straight to the cart and
 * continue toward payment. Renders nothing when the cart is empty.
 *
 * Place it as the LAST child inside a page's <Container>: it emits a spacer in
 * normal flow (so the last content can scroll clear of the fixed bar) plus the
 * fixed bar itself.
 */

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography, Badge } from '@mui/material';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

import { selectCartItems, selectCartCount } from '@/store/cartSlice';
import { selectLanguage } from '@/store/uiSlice';
import { formatToman, computeProforma } from '@/utils/currency';

export default function CartBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const items = useSelector(selectCartItems);
  const count = useSelector(selectCartCount);

  if (count === 0) return null;

  const base = items.reduce((sum, it) => sum + (Number(it.priceToman) || 0), 0);
  const total = computeProforma(base).totalAmount;

  return (
    <>
      {/* Spacer so the fixed bar never covers the last bit of content. */}
      <Box aria-hidden sx={{ height: 88 }} />

      <Box
        sx={{
          position: 'fixed',
          insetInline: 0,
          bottom: 0,
          zIndex: (th) => th.zIndex.appBar,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 -6px 24px rgba(0,0,0,0.10)',
          pt: 1.25,
          pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          px: 2,
        }}
      >
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => navigate('/cart')}
            sx={{ py: 1.25 }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Badge badgeContent={count} color="error">
                  <ShoppingCartCheckoutIcon />
                </Badge>
                <Typography fontWeight={700}>{t('cart.goToCart')}</Typography>
              </Stack>
              <Typography fontWeight={700}>{formatToman(total, language)}</Typography>
            </Stack>
          </Button>
        </Box>
      </Box>
    </>
  );
}
